import axios from "axios";
import { useAuthStore } from "@/features/auth/auth.store";
import { BASE_URL } from "../consts/api";

function getClientTimezone(): string | null {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
	} catch {
		return null;
	}
}

export const defaultClient = axios.create({
	baseURL: BASE_URL,
	timeout: 10000,
	headers: {
		"Content-Type": "application/json",
	},
});

export const authClient = axios.create({
	baseURL: BASE_URL,
	timeout: 10000,
	headers: {
		"Content-Type": "application/json",
	},
});

defaultClient.interceptors.request.use(
	(config) => {
		const timezone = getClientTimezone();
		if (timezone) {
			config.headers = config.headers ?? {};
			config.headers["x-timezone"] = timezone;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

authClient.interceptors.request.use(
	(config) => {
		const timezone = getClientTimezone();
		const token = useAuthStore.getState().accessToken;
		config.headers = config.headers ?? {};
		if (timezone) {
			config.headers["x-timezone"] = timezone;
		}
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

defaultClient.interceptors.response.use(
	(response) => response,
	(error) => Promise.reject(error),
);

let isRefreshing = false;
let pendingRequests: ((token: string) => void)[] = [];

authClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		if (error.response?.status !== 401 || originalRequest._retry) {
			return Promise.reject(error);
		}

		const refreshToken = useAuthStore.getState().refreshToken;
		if (!refreshToken) {
			useAuthStore.getState().clear();
			return Promise.reject(error);
		}

		if (isRefreshing) {
			return new Promise((resolve) => {
				pendingRequests.push((token: string) => {
					originalRequest.headers.Authorization = `Bearer ${token}`;
					resolve(authClient(originalRequest));
				});
			});
		}

		originalRequest._retry = true;
		isRefreshing = true;

		try {
			const { data } = await defaultClient.post("/auth/refresh", { refreshToken });
			const newToken = data.accessToken;
			useAuthStore.getState().setAccessToken(newToken);

			pendingRequests.forEach((cb) => cb(newToken));
			pendingRequests = [];

			originalRequest.headers.Authorization = `Bearer ${newToken}`;
			return authClient(originalRequest);
		} catch {
			useAuthStore.getState().clear();
			return Promise.reject(error);
		} finally {
			isRefreshing = false;
		}
	},
);
